// utils/developerTokenManager.ts - OPTIMIZED VERSION

import { BASE_URL, API_URLS } from "../api/urls";

interface DeveloperAccessToken {
  token: string;
  email: string;
  cardId?: number;
  receivedAt: string;
  lastUsed: string;
}

interface StoredDeveloperTokens {
  [email: string]: DeveloperAccessToken[];
}

interface DeveloperUrlResponse {
  success: boolean;
  developer_email: string;
  share_url: string;
}

export class DeveloperTokenManager {
  private static STORAGE_KEY = "developer_access_tokens";
  private static TOKEN_EXPIRY_DAYS = 30;
  
  
  // ✅ NEW: In-memory cache to prevent redundant API calls
  private static tokenCache: Map<string, { token: string; timestamp: number }> = new Map();
  private static CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

  /**
   * ✅ OPTIMIZED: Get developer token with in-memory caching
   */
  static async getDeveloperTokenForEmail(email: string): Promise<string | null> {
    try {
      console.log("🔍 Getting developer token for:", email);
      
      // ✅ CHECK 1: In-memory cache (fastest)
      const cached = this.tokenCache.get(email);
      if (cached && Date.now() - cached.timestamp < this.CACHE_DURATION) {
        console.log("✅ Using cached token (in-memory)");
        return cached.token;
      }
      
      // ✅ CHECK 2: localStorage (fast)
      const storedToken = this.getTokenForEmail(email);
      if (storedToken && this.isTokenValid(email, storedToken)) {
        console.log("✅ Using valid token from localStorage");
        // Cache it for next time
        this.tokenCache.set(email, { token: storedToken, timestamp: Date.now() });
        return storedToken;
      }
      
      // ✅ CHECK 3: API call (slowest - only if needed)
      console.log("⚠️ No valid cached token, fetching from API...");
      const apiToken = await this.fetchDeveloperTokenFromApi(email);
      
      if (apiToken) {
        console.log("✅ Using fresh token from API");
        // Cache it
        this.tokenCache.set(email, { token: apiToken, timestamp: Date.now() });
        return apiToken;
      }
      
      console.log("❌ No developer token available");
      return null;
      
    } catch (error) {
      console.error("❌ Error getting developer token:", error);
      
      // Final fallback to localStorage
      return this.getTokenForEmail(email);
    }
  }

  /**
   * ✅ NEW: Clear in-memory cache
   */
  static clearCache() {
    this.tokenCache.clear();
    console.log("✅ Token cache cleared");
  }

  /**
   * ✅ NEW: Prefetch token for better UX (call early in app lifecycle)
   */
  static async prefetchToken(email: string): Promise<void> {
    try {
      await this.getDeveloperTokenForEmail(email);
      console.log("✅ Token prefetched for:", email);
    } catch (error) {
      console.warn("⚠️ Token prefetch failed:", error);
    }
  }

  // ... (keep all existing methods unchanged)
  
  private static extractTokenFromUrl(shareUrl: string): { token: string; email: string } | null {
    try {
      const url = new URL(shareUrl);
      const code = url.searchParams.get('code');
      const developer = url.searchParams.get('developer');

      if (code && developer) {
        return {
          token: code,
          email: decodeURIComponent(developer)
        };
      }

      console.error("❌ Code or developer email not found in URL");
      return null;
    } catch (error) {
      console.error("❌ Error parsing share URL:", error);
      return null;
    }
  }

  static async fetchDeveloperTokenFromApi(currentUserEmail: string): Promise<string | null> {
    try {
      console.log("🔄 Fetching developer URL from API for:", currentUserEmail);
      
      const accessToken = localStorage.getItem("access_token") || 
                         (window as any).__feebdack_tokens?.access_token;
      
      if (!accessToken) {
        console.error("❌ No access token available for API call");
        return null;
      }

      const apiUrl = `${BASE_URL}/feedback/get_shared_url?developer_email=${encodeURIComponent(currentUserEmail)}`;
      
      const response = await fetch(apiUrl, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        if (response.status === 404) {
          console.log("ℹ️ No developer access found for this email");
          return null;
        }
        
        const errorText = await response.text();
        console.error("❌ API error:", {
          status: response.status,
          body: errorText,
        });
        return null;
      }

      const data: DeveloperUrlResponse = await response.json();
      
      if (!data.success || !data.share_url) {
        console.error("❌ Invalid API response:", data);
        return null;
      }

      console.log("✅ Developer URL fetched successfully");

      const extracted = this.extractTokenFromUrl(data.share_url);

      if (!extracted) {
        console.error("❌ Failed to extract code from URL");
        return null;
      }

      // Exchange the share code for an actual access token
      const verifyResponse = await fetch(API_URLS.VERIFY_DEVELOPER_TOKEN, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ code: extracted.token }),
      });

      if (!verifyResponse.ok) {
        console.error("❌ Failed to verify share code:", verifyResponse.status);
        return null;
      }

      const verifyData = await verifyResponse.json();
      const developerAccessToken: string = verifyData.access_token;

      this.storeToken(developerAccessToken, extracted.email);

      console.log("✅ Developer access token obtained and stored");
      return developerAccessToken;
      
    } catch (error) {
      console.error("❌ Error fetching developer token from API:", error);
      return null;
    }
  }

  static storeToken(token: string, email: string, cardId?: number): void {
    try {
      const normalizedEmail = email.toLowerCase().trim();
      const allTokens = this.getAllTokens();

      if (!allTokens[normalizedEmail]) {
        allTokens[normalizedEmail] = [];
      }

      const existingTokenIndex = allTokens[normalizedEmail].findIndex(
        (t) => t.token === token
      );

      const tokenData: DeveloperAccessToken = {
        token,
        email: normalizedEmail,
        cardId,
        receivedAt:
          existingTokenIndex >= 0
            ? allTokens[normalizedEmail][existingTokenIndex].receivedAt
            : new Date().toISOString(),
        lastUsed: new Date().toISOString(),
      };

      if (existingTokenIndex >= 0) {
        allTokens[normalizedEmail][existingTokenIndex] = tokenData;
        console.log("✅ Updated existing developer token for:", normalizedEmail);
      } else {
        allTokens[normalizedEmail].push(tokenData);
        console.log("✅ Stored new developer token for:", normalizedEmail);
      }

      this.cleanupExpiredTokens(allTokens);
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(allTokens));
      localStorage.setItem("last_developer_email", normalizedEmail);
      
      // ✅ Update cache
      this.tokenCache.set(normalizedEmail, { token, timestamp: Date.now() });
      
    } catch (error) {
      console.error("❌ Error storing developer token:", error);
    }
  }

  static getTokenForEmail(email: string): string | null {
    try {
      const normalizedEmail = email.toLowerCase().trim();
      const allTokens = this.getAllTokens();
      const emailTokens = allTokens[normalizedEmail];

      if (!emailTokens || emailTokens.length === 0) {
        return null;
      }

      const sortedTokens = emailTokens.sort(
        (a, b) =>
          new Date(b.lastUsed).getTime() - new Date(a.lastUsed).getTime()
      );

      return sortedTokens[0].token;
    } catch (error) {
      console.error("❌ Error retrieving developer token:", error);
      return null;
    }
  }

  static async getTokenForCard(email: string, cardId: number): Promise<string | null> {
    try {
      const normalizedEmail = email.toLowerCase().trim();
      
      const apiToken = await this.getDeveloperTokenForEmail(normalizedEmail);
      if (apiToken) {
        return apiToken;
      }
      
      const allTokens = this.getAllTokens();
      const emailTokens = allTokens[normalizedEmail];

      if (!emailTokens || emailTokens.length === 0) {
        return null;
      }

      const cardToken = emailTokens.find((t) => t.cardId === cardId);
      if (cardToken) {
        cardToken.lastUsed = new Date().toISOString();
        localStorage.setItem(this.STORAGE_KEY, JSON.stringify(allTokens));
        return cardToken.token;
      }

      return this.getTokenForEmail(email);
    } catch (error) {
      console.error("❌ Error retrieving card-specific token:", error);
      return null;
    }
  }

  private static getAllTokens(): StoredDeveloperTokens {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (stored && stored !== "null") {
        return JSON.parse(stored);
      }
      return {};
    } catch (error) {
      console.error("❌ Error reading stored tokens:", error);
      return {};
    }
  }

  static getAllTokensForEmail(email: string): DeveloperAccessToken[] {
    const normalizedEmail = email.toLowerCase().trim();
    const allTokens = this.getAllTokens();
    return allTokens[normalizedEmail] || [];
  }

  private static cleanupExpiredTokens(tokens: StoredDeveloperTokens): void {
    const now = new Date();
    const expiryDate = new Date(
      now.getTime() - this.TOKEN_EXPIRY_DAYS * 24 * 60 * 60 * 1000
    );

    Object.keys(tokens).forEach((email) => {
      tokens[email] = tokens[email].filter((token) => {
        const receivedDate = new Date(token.receivedAt);
        return receivedDate > expiryDate;
      });

      if (tokens[email].length === 0) {
        delete tokens[email];
      }
    });
  }

  static removeToken(email: string, token: string): void {
    try {
      const normalizedEmail = email.toLowerCase().trim();
      const allTokens = this.getAllTokens();

      if (allTokens[normalizedEmail]) {
        allTokens[normalizedEmail] = allTokens[normalizedEmail].filter(
          (t) => t.token !== token
        );

        if (allTokens[normalizedEmail].length === 0) {
          delete allTokens[normalizedEmail];
        }

        localStorage.setItem(this.STORAGE_KEY, JSON.stringify(allTokens));
        
        // ✅ Clear from cache
        this.tokenCache.delete(normalizedEmail);
        
        console.log("✅ Removed developer token for:", normalizedEmail);
      }
    } catch (error) {
      console.error("❌ Error removing developer token:", error);
    }
  }

  static removeAllTokensForEmail(email: string): void {
    try {
      const normalizedEmail = email.toLowerCase().trim();
      const allTokens = this.getAllTokens();

      delete allTokens[normalizedEmail];
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(allTokens));
      
      // ✅ Clear from cache
      this.tokenCache.delete(normalizedEmail);
      
      console.log("✅ Removed all developer tokens for:", normalizedEmail);
    } catch (error) {
      console.error("❌ Error removing developer tokens:", error);
    }
  }

  static clearAllTokens(): void {
    try {
      localStorage.removeItem(this.STORAGE_KEY);
      localStorage.removeItem("last_developer_email");
      
      // ✅ Clear cache
      this.clearCache();
      
      console.log("✅ Cleared all developer tokens");
    } catch (error) {
      console.error("❌ Error clearing developer tokens:", error);
    }
  }

  static isTokenValid(email: string, token: string): boolean {
    const allTokens = this.getAllTokensForEmail(email);
    const tokenData = allTokens.find((t) => t.token === token);

    if (!tokenData) {
      return false;
    }

    const receivedDate = new Date(tokenData.receivedAt);
    const now = new Date();
    const daysSinceReceived =
      (now.getTime() - receivedDate.getTime()) / (1000 * 60 * 60 * 24);

    return daysSinceReceived < this.TOKEN_EXPIRY_DAYS;
  }

  static debugTokens(): void {
    const allTokens = this.getAllTokens();
    console.group("🔐 Developer Tokens Debug");
    console.log("Total emails with tokens:", Object.keys(allTokens).length);
    console.log("Cache size:", this.tokenCache.size);
    Object.entries(allTokens).forEach(([email, tokens]) => {
      console.log(`\n${email}:`);
      tokens.forEach((token, index) => {
        console.log(`  Token ${index + 1}:`, {
          tokenPrefix: token.token.substring(0, 15) + "...",
          cardId: token.cardId || "none",
          receivedAt: token.receivedAt,
          lastUsed: token.lastUsed,
          isValid: this.isTokenValid(email, token.token),
        });
      });
    });
    console.groupEnd();
  }
}